// src/components/admin/CampEnrollmentManager.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Lock, Unlock, Users, AlertCircle, Check } from 'lucide-react';
import { styles } from '../../styles/theme';
import { collection, getDocs, getDoc, addDoc, updateDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

const CampEnrollmentManager = ({ onBack }) => {
  const [enrollmentData, setEnrollmentData] = useState([]);
  const [students, setStudents] = useState([]); // eslint-disable-line no-unused-vars
  const [settings, setSettings] = useState({ defaultCapacity: 20 });
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(getStartOfWeek(new Date()));
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  function getStartOfWeek(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  const formatDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}-${day}-${year}`;
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load settings
      const settingsDoc = await getDoc(doc(db, 'settings', 'camp_enrollment'));
      if (settingsDoc.exists()) {
        setSettings(settingsDoc.data());
      }

      // Generate week dates
      const weekDates = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(selectedWeek);
        d.setDate(selectedWeek.getDate() + i);
        return formatDate(d);
      });

      // Load enrollment availability data
      const availabilityRef = collection(db, 'camp_availability');
      const availabilitySnapshot = await getDocs(availabilityRef);
      const availabilityMap = {};
      
      availabilitySnapshot.docs.forEach(doc => {
        availabilityMap[doc.data().date] = {
          id: doc.id,
          ...doc.data()
        };
      });

      // Load students to count enrollments
      const studentsRef = collection(db, 'students');
      const studentsSnapshot = await getDocs(studentsRef);
      const allStudents = studentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStudents(allStudents);

      // Build enrollment data for the week
      const enrollmentDataForWeek = weekDates.map(dateStr => {
        // Count enrolled students for this date
        const enrolledCount = allStudents.filter(student => {
          if (!student.programs || !Array.isArray(student.programs)) return false;
          return student.programs.some(program => 
            program.type === 'Full-Day Camp' && 
            program.selectedDates && 
            program.selectedDates.includes(dateStr)
          );
        }).length;

        // Get availability data or create default
        const availability = availabilityMap[dateStr] || {
          date: dateStr,
          maxCapacity: settings.defaultCapacity,
          isOpen: false, // Default to closed
          manualOverride: false
        };

        const isFull = enrolledCount >= availability.maxCapacity;

        return {
          ...availability,
          enrolledCount,
          isFull,
          availableSpots: Math.max(0, availability.maxCapacity - enrolledCount)
        };
      });

      setEnrollmentData(enrollmentDataForWeek);
    } catch (error) {
      console.error('Error loading enrollment data:', error);
      alert('Failed to load enrollment data');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings) => {
    try {
      await setDoc(doc(db, 'settings', 'camp_enrollment'), newSettings);
      setSettings(newSettings);
      alert('Settings saved!');
      setShowSettingsModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    }
  };

  const toggleDateAvailability = async (dateStr, currentStatus) => {
    try {
      const existingData = enrollmentData.find(d => d.date === dateStr);
      
      if (existingData && existingData.id) {
        // Update existing
        await updateDoc(doc(db, 'camp_availability', existingData.id), {
          isOpen: !currentStatus,
          manualOverride: true,
          updatedAt: new Date().toISOString()
        });
      } else {
        // Create new
        await addDoc(collection(db, 'camp_availability'), {
          date: dateStr,
          isOpen: !currentStatus,
          maxCapacity: settings.defaultCapacity,
          manualOverride: true,
          createdAt: new Date().toISOString()
        });
      }
      
      await loadData();
      alert(`Date ${!currentStatus ? 'opened' : 'closed'} for enrollment!`);
    } catch (error) {
      console.error('Error toggling availability:', error);
      alert('Failed to update availability');
    }
  };

  const bulkOpenWeek = async () => {
    if (!window.confirm('Open ALL weekdays this week for enrollment?')) return;

    try {
      const weekdays = enrollmentData.filter((_, idx) => idx < 5); // Mon-Fri only
      
      for (const day of weekdays) {
        if (day.id) {
          await updateDoc(doc(db, 'camp_availability', day.id), {
            isOpen: true,
            manualOverride: true,
            updatedAt: new Date().toISOString()
          });
        } else {
          await addDoc(collection(db, 'camp_availability'), {
            date: day.date,
            isOpen: true,
            maxCapacity: settings.defaultCapacity,
            manualOverride: true,
            createdAt: new Date().toISOString()
          });
        }
      }
      
      await loadData();
      alert('Week opened for enrollment!');
    } catch (error) {
      console.error('Error opening week:', error);
      alert('Failed to open week');
    }
  };

  const bulkCloseWeek = async () => {
    if (!window.confirm('Close ALL dates this week for enrollment?')) return;

    try {
      for (const day of enrollmentData) {
        if (day.id) {
          await updateDoc(doc(db, 'camp_availability', day.id), {
            isOpen: false,
            manualOverride: true,
            updatedAt: new Date().toISOString()
          });
        } else {
          await addDoc(collection(db, 'camp_availability'), {
            date: day.date,
            isOpen: false,
            maxCapacity: settings.defaultCapacity,
            manualOverride: true,
            createdAt: new Date().toISOString()
          });
        }
      }
      
      await loadData();
      alert('Week closed for enrollment!');
    } catch (error) {
      console.error('Error closing week:', error);
      alert('Failed to close week');
    }
  };

  const updateCapacity = async (dateStr, newCapacity) => {
    try {
      const existingData = enrollmentData.find(d => d.date === dateStr);
      
      if (existingData && existingData.id) {
        await updateDoc(doc(db, 'camp_availability', existingData.id), {
          maxCapacity: parseInt(newCapacity),
          updatedAt: new Date().toISOString()
        });
      } else {
        await addDoc(collection(db, 'camp_availability'), {
          date: dateStr,
          maxCapacity: parseInt(newCapacity),
          isOpen: false,
          createdAt: new Date().toISOString()
        });
      }
      
      await loadData();
    } catch (error) {
      console.error('Error updating capacity:', error);
      alert('Failed to update capacity');
    }
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(selectedWeek);
    d.setDate(selectedWeek.getDate() + i);
    return {
      date: d,
      dateString: formatDate(d),
      dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
      dayNumber: d.getDate(),
      fullDate: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    };
  });

  return (
    <div style={{ padding: '1rem', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={onBack} style={styles.secondaryButton}>
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
              <Users style={{ display: 'inline', marginRight: '0.5rem' }} size={24} />
              Camp Enrollment Manager
            </h1>
            <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
              Control availability and capacity for Full-Day Camp registration
            </p>
          </div>

          {/* Week Navigation & Actions */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                const prevWeek = new Date(selectedWeek);
                prevWeek.setDate(prevWeek.getDate() - 7);
                setSelectedWeek(prevWeek);
              }}
              style={{...styles.button, backgroundColor: '#6b7280', padding: '0.5rem 1rem'}}
            >
              ← Prev Week
            </button>
            <span style={{ fontWeight: '500', padding: '0 1rem' }}>
              {selectedWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - 
              {' '}{new Date(selectedWeek.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            <button
              onClick={() => {
                const nextWeek = new Date(selectedWeek);
                nextWeek.setDate(nextWeek.getDate() + 7);
                setSelectedWeek(nextWeek);
              }}
              style={{...styles.button, padding: '0.5rem 1rem'}}
            >
              Next Week →
            </button>
            <button
              onClick={bulkOpenWeek}
              style={{...styles.button, backgroundColor: '#10b981', padding: '0.5rem 1rem', marginLeft: '1rem'}}
            >
              <Unlock size={16} style={{ marginRight: '0.25rem' }} />
              Open Week
            </button>
            <button
              onClick={bulkCloseWeek}
              style={{...styles.button, backgroundColor: '#ef4444', padding: '0.5rem 1rem'}}
            >
              <Lock size={16} style={{ marginRight: '0.25rem' }} />
              Close Week
            </button>
            <button
              onClick={() => setShowSettingsModal(true)}
              style={{...styles.button, backgroundColor: '#8b5cf6', padding: '0.5rem 1rem'}}
            >
              ⚙️ Settings
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>Loading enrollment data...</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {/* Enrollment Grid */}
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse', 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            overflow: 'hidden',
            border: '2px solid #e5e7eb'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600' }}>Date</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Enrolled</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Capacity</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Available Spots</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {weekDays.slice(0, 5).map((day, idx) => {
                const dayData = enrollmentData[idx] || {};
                const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;
                
                if (isWeekend) {
                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: '#f9fafb' }}>
                      <td colSpan="6" style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af' }}>
                        {day.dayName} {day.fullDate} - Weekend (No Camp)
                      </td>
                    </tr>
                  );
                }

                const statusColor = dayData.isOpen 
                  ? (dayData.isFull ? '#f59e0b' : '#10b981')
                  : '#ef4444';
                
                const statusText = dayData.isOpen
                  ? (dayData.isFull ? '⚠️ Full' : '✅ Open')
                  : '🔒 Closed';

                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ fontWeight: '600' }}>{day.dayName}</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{day.fullDate}</div>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{ 
                        padding: '0.25rem 0.75rem', 
                        borderRadius: '9999px', 
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        backgroundColor: statusColor + '20',
                        color: statusColor
                      }}>
                        {statusText}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', fontSize: '1.25rem', fontWeight: '600' }}>
                      {dayData.enrolledCount || 0}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <input
                        type="number"
                        value={dayData.maxCapacity || settings.defaultCapacity}
                        onChange={(e) => updateCapacity(day.dateString, e.target.value)}
                        style={{ 
                          width: '60px', 
                          padding: '0.25rem', 
                          textAlign: 'center',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px'
                        }}
                        min="0"
                      />
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <span style={{ 
                        fontSize: '1.25rem', 
                        fontWeight: '600',
                        color: dayData.availableSpots > 5 ? '#10b981' : dayData.availableSpots > 0 ? '#f59e0b' : '#ef4444'
                      }}>
                        {dayData.availableSpots || 0}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <button
                        onClick={() => toggleDateAvailability(day.dateString, dayData.isOpen)}
                        style={{
                          ...styles.button,
                          backgroundColor: dayData.isOpen ? '#ef4444' : '#10b981',
                          padding: '0.5rem 1rem',
                          fontSize: '0.875rem'
                        }}
                      >
                        {dayData.isOpen ? (
                          <><Lock size={14} style={{ marginRight: '0.25rem' }} /> Close</>
                        ) : (
                          <><Unlock size={14} style={{ marginRight: '0.25rem' }} /> Open</>
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Info Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <Users size={20} style={{ color: '#3b82f6', marginRight: '0.5rem' }} />
                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280' }}>Total Enrolled (Week)</h3>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                {enrollmentData.slice(0, 5).reduce((sum, day) => sum + (day.enrolledCount || 0), 0)}
              </div>
            </div>

            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <Check size={20} style={{ color: '#10b981', marginRight: '0.5rem' }} />
                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280' }}>Open Days</h3>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                {enrollmentData.slice(0, 5).filter(day => day.isOpen).length} / 5
              </div>
            </div>

            <div style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                <AlertCircle size={20} style={{ color: '#f59e0b', marginRight: '0.5rem' }} />
                <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#6b7280' }}>Available Spots (Week)</h3>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                {enrollmentData.slice(0, 5).reduce((sum, day) => sum + (day.availableSpots || 0), 0)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', width: '90%', maxWidth: '400px' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem' }}>
              ⚙️ Enrollment Settings
            </h2>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Default Daily Capacity
              </label>
              <input
                type="number"
                value={settings.defaultCapacity}
                onChange={(e) => setSettings({ ...settings, defaultCapacity: parseInt(e.target.value) })}
                style={{...styles.input, padding: '0.75rem'}}
                min="1"
              />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                This will be the default capacity for new dates
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSettingsModal(false)}
                style={{...styles.button, backgroundColor: '#6b7280'}}
              >
                Cancel
              </button>
              <button
                onClick={() => saveSettings(settings)}
                style={styles.button}
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampEnrollmentManager;